import React, { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WorkflowLanding from '../../components/WorkflowLanding/WorkflowLanding';
import Modal from '../../../CommonComponents/Modal';
import { PrimaryButton, SecondaryButton } from '../../../CommonComponents/Button';
import Spinner from '../../../CommonComponents/Spinner';
import { Alert } from '../../../CommonComponents/Alert/Alert';
import {
  acceptTermAndConditions,
  setAcceptingTermsErrors,
  setSubscriberEnrolling,
} from '../../ExperianActions';
import {
  acceptingTermsErrorsSelector,
  isAcceptingTermsSelector,
  subscriberEnrollingSelector,
} from '../../ExperianSelector';
import ExperianContext from '../../ExperianContext';
import { Heading as Heading1, ModalFooter, SubHeading } from '../../ExperianStyles';
import {
  Wrapper,
  TermsContainer,
  Paragraph,
  Heading2,
  Heading4,
  Strong,
  Heading3,
} from './TermsStyles';

const Terms = () => {
  const subscriberEnrolling = useSelector(subscriberEnrollingSelector);
  const isAcceptingTerms = useSelector(isAcceptingTermsSelector);
  const acceptingTermsErrors = useSelector(acceptingTermsErrorsSelector);

  const { clientId, subscriberId } = useContext(ExperianContext);

  const dispatch = useDispatch();

  const acceptTermAndConditionsCallback = useCallback(() => {
    dispatch(setAcceptingTermsErrors([]));
    dispatch(acceptTermAndConditions(clientId, subscriberId));
  }, [clientId, subscriberId, dispatch]);

  const onRequestClose = useCallback(() => {
    dispatch(setSubscriberEnrolling(''));
    dispatch(setAcceptingTermsErrors([]));
  }, [dispatch]);

  return (
    <>
      {isAcceptingTerms && <Spinner />}
      <WorkflowLanding />
      <Modal isOpen={subscriberEnrolling === subscriberId}>
        <Wrapper>
          {acceptingTermsErrors.length > 0 && <Alert message={acceptingTermsErrors} />}
          <Heading1>Enroll in Online Identity and Financial Account Monitoring Services</Heading1>
          <SubHeading>Terms and Conditions</SubHeading>
          <TermsContainer>
            <Paragraph>
              Your use of this service means that you have accepted these terms in their entirety.
              If you do not agree with these terms in their entirety, please terminate the service
              immediately. These terms (hereinafter referred to as “Terms and Conditions”) apply to
              your use of any products and/or services (the “Services”) offered or provided by CSID
              (as hereinafter defined), Orion Advisor Technology, LLC (“Orion”), or their respective
              corporate affiliates.
            </Paragraph>

            <Heading2>USE OF SERVICES:</Heading2>
            <Paragraph>
              Adviser agrees that Adviser clients will use Services only for their benefit. Adviser
              will be responsible for all use of Adviser’s clients’ membership numbers and must
              notify Orion immediately of any unauthorized use of Adviser’s clients’ membership
              numbers, or the theft or misplacement of a membership number.
            </Paragraph>
            <Paragraph>
              Adviser understands that by enrolling for the Services, Adviser’s clients are
              providing &quot;written instructions&quot; in accordance with the federal Fair Credit
              Reporting Act, as amended (&quot;FCRA&quot;), for Orion and its service providers,
              which may include CSIdentity Corporation (“CSID”), to obtain information from
              Adviser’s clients’ personal credit profile from Experian, Equifax, and Transunion, the
              three major credit reporting agencies. Adviser authorizes Orion and its service
              providers to use an Adviser’s client’s social security number to access an Adviser’s
              client’s personal credit profile, to verify identity, and to provide credit
              monitoring, reporting and scoring products.
            </Paragraph>

            <Heading2>DISPUTE RESOLUTION BY BINDING ARBITRATION:</Heading2>
            <Paragraph>PLEASE READ THIS CAREFULLY.</Paragraph>
            <Paragraph>IT AFFECTS YOUR RIGHTS.</Paragraph>
            <Paragraph>SUMMARY:</Paragraph>
            <Paragraph>
              MOST CUSTOMER CONCERNS CAN BE RESOLVED QUICKLY AND TO THE CUSTOMER&apos;S SATISFACTION
              BY CALLING CUSTOMER SERVICE AT 877-899-1115. IN THE UNLIKELY EVENT THAT CUSTOMER
              SERVICE IS UNABLE TO RESOLVE A COMPLAINT YOU MAY HAVE WITH ORION OR ORION’S SERVICE
              PROVIDER(S) TO YOUR SATISFACTION (OR IF ORION AND/OR ORION’S SERVICE PROVIDER(S) HAVE
              NOT BEEN ABLE TO RESOLVE A DISPUTE WITH YOU AFTER ATTEMPTING TO DO SO INFORMALLY),
              YOU, ON THE ONE HAND, AND ORION AND/OR ORION’S SERVICE PROVIDER(S), ON THE OTHER, EACH
              AGREE TO RESOLVE THOSE DISPUTES THROUGH BINDING ARBITRATION OR SMALL CLAIMS COURT
              INSTEAD OF IN COURTS OF GENERAL JURISDICTION TO THE FULLEST EXTENT PERMITTED BY LAW.
            </Paragraph>
            <Paragraph>
              ARBITRATION IS MORE INFORMAL THAN A LAWSUIT IN COURT. ARBITRATION USES A NEUTRAL
              ARBITRATOR INSTEAD OF A JUDGE OR JURY, ALLOWS FOR MORE LIMITED DISCOVERY THAN IN
              COURT, AND IS SUBJECT TO VERY LIMITED REVIEW BY COURTS. ARBITRATORS CAN AWARD THE SAME
              DAMAGES AND RELIEF THAT A COURT CAN AWARD. ANY ARBITRATION UNDER THESE TERMS AND
              CONDITIONS WILL TAKE PLACE ON AN INDIVIDUAL BASIS; CLASS ARBITRATIONS AND CLASS
              ACTIONS ARE NOT PERMITTED. WE WILL PAY ALL COSTS OF ARBITRATION, NO MATTER WHO WINS,
              SO LONG AS YOUR CLAIM IS NOT FRIVOLOUS. HOWEVER, IN ARBITRATION, YOU, ON THE ONE HAND,
              AND ORION AND/OR ORION’S SERVICE PROVIDER(S), ON THE OTHER, WOULD BE ENTITLED TO
              RECOVER ATTORNEYS&apos; FEES FROM EACH OTHER PARTY TO THE SAME EXTENT AS YOU AND THEY
              WOULD BE IN COURT.
            </Paragraph>

            <Heading4>Arbitration Agreement:</Heading4>
            <Paragraph>
              <Strong>(B) ARBITRATION</Strong> - You, on the one hand, and Orion and/or Orion’s
              service provider(s), on the other, agree that any claim or dispute (&quot;Claim&quot;)
              between us shall, at the election of any one of us, be resolved by binding arbitration
              administered by the American Arbitration Association (&quot;AAA&quot;) under its rules
              for consumer arbitrations. It is the parties&apos; intent that this arbitration
              provision be construed broadly, including that this arbitration provision includes any
              Claims by you against Orion or Orion’s service provider(s) as well as their respective
              corporate affiliates for claims arising out of these Terms and Conditions directly
              related to the services or product/service websites. However, any disputes or claims
              you may have which relate to your credit report, or any claims arising out of or
              relating to the Fair Credit Reporting Act (&quot;FCRA&quot;) and/or the FCRA&apos;s
              state law equivalent(s), are not subject to or governed by this agreement to
              arbitrate.
            </Paragraph>
            <Paragraph>
              You agree that, by entering into these Terms and Conditions, you, Orion and Orion’s
              service provider(s) are each waiving the right to a trial by jury or to participate in
              a class action. At your request, we will promptly reimburse you for your payment of
              your arbitration filing fee. (The filing fee currently is $200 for claims under
              $10,000 but is subject to change by the arbitration provider. If you are unable to pay
              this fee, we will pay it directly after receiving a written request). The arbitrator
              may award declaratory or injunctive relief only in favor of the individual party
              seeking relief and only to the extent necessary to provide relief warranted by that
              party&apos;s individual Claim. YOU, ON ONE HAND, AND ORION AND/OR ORION’S SERVICE
              PROVIDER(S), ON THE OTHER, AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN
              YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY
              PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
            </Paragraph>
            <Paragraph>
              Further, unless you, on one hand, and Orion and/or Orion’s service provider(s), on the
              other, agree otherwise, the arbitrator may not consolidate more than one person&apos;s
              Claims, and may not otherwise preside over any form of a representative or class
              proceeding. If this specific provision in the preceding sentence is found to be
              unenforceable, then the entirety of this arbitration provision in this section shall
              be null and void. Notwithstanding any of the foregoing provisions, any party may bring
              an individual action in small claims court. The parties to the Terms and Conditions
              acknowledge that this arbitration agreement is made pursuant to a transaction
              involving interstate commerce, and shall be governed by the Federal Arbitration Act, 9
              U.S.C. § 1 et seq. (&quot;FAA&quot;). Judgment upon any arbitration award may be
              entered in any court having jurisdiction. The arbitrator shall follow substantive law
              to the extent consistent with the FAA and shall honor any claims or privileges
              recognized by law. The terms of this section shall survive any termination,
              cancellation or expiration of the Terms and Conditions.
            </Paragraph>
            <Paragraph>
              <Strong>LIABILITY</Strong> - NEITHER ORION, CSID, NOR ANY OF THEIR RESPECTIVE
              AFFILIATES SHALL HAVE ANY LIABILITY TO YOU AS AN AGENT IN OBTAINING COPIES OF: YOUR
              PERSONAL CREDIT REPORT, CREDIT ALERT REPORT, QUARTERLY UPDATE, OR CREDIT SCORE.
              NEITHER ORION, CSID, NOR ANY OF THEIR RESPECTIVE AFFILIATES OR CREDIT INFORMATION
              SUBCONTRACTORS MAKE ANY WARRANTY, EXPRESS OR IMPLIED, FOR THE ACCURACY OF THE
              INFORMATION CONTAINED IN OR PROVIDED IN CONJUNCTION WITH THE SERVICES. NEITHER ORION,
              CSID, NOR ANY OF THEIR RESPECTIVE AFFILIATES ASSUME ANY LIABILITY FOR DAMAGES, DIRECT
              OR INDIRECT, CONSEQUENTIAL OR INCIDENTAL, IN CONNECTION WITH THE PERFORMANCE OF THE
              SERVICES OR YOUR REQUEST, USE OR ATTEMPTED USE OF THE SERVICES. NEITHER ORION, NOR ANY
              OF THEIR RESPECTIVE AFFILIATES OR CREDIT INFORMATION SUBCONTRACTORS ARE RESPONSIBLE
              FOR NEGATIVE FACTUAL INFORMATION CONTAINED IN ANY REPORTS YOU RECEIVE AS PART OF THE
              SERVICE. THE AGGREGATE LIABILITY OF ALL SUCH PARTIES TO YOU IN ANY EVENT IS LIMITED TO
              THE AMOUNT WHICH YOU HAVE PAID ORION FOR YOUR MEMBERSHIP. THE SERVICES ARE NOT CREDIT
              COUNSELING SERVICES AND DO NOT PROMISE TO HELP YOU OBTAIN A LOAN OR IMPROVE YOUR
              CREDIT RECORD, HISTORY, OR RATING. THE TERMS OF THIS SECTION SHALL SURVIVE ANY
              TERMINATION, CANCELLATION, OR EXPIRATION OF THE TERMS AND CONDITIONS.
            </Paragraph>

            <Heading3>VANTAGESCORE 3.0 CREDIT SCORE </Heading3>
            <Paragraph>
              VantageScore 3.0, with scores ranging from 300 to 850, is a user-friendly credit score
              model developed by the three major nationwide credit reporting agencies, Experian®,
              TransUnion®, and Equifax®. VantageScore 3.0 is used by some but not all lenders.
              Higher scores represent a greater likelihood that you&apos;ll pay back your debts so
              you are viewed as being a lower credit risk to lenders. A lower score indicates to
              lenders that you may be a higher credit risk.
            </Paragraph>
            <Paragraph>
              There are three different major credit reporting agencies, Experian, TransUnion, and
              Equifax that maintain a record of your credit history known as your credit file.
              Credit scores are based on the information in your credit file at the time it is
              requested. Your credit file information can vary from agency to agency because some
              lenders report your credit history to only one or two of the agencies. So your credit
              scores can vary if the information they have on file for you is different. Since the
              information in your file can change over time, your credit scores also may be
              different from day-to-day. Different credit scoring models can also give a different
              assessment of the credit risk (risk of default) for the same consumer and same credit
              file.
            </Paragraph>
            <Paragraph>
              There are different credit scoring models which may be used by lenders and insurers.
              Your lender may not use VantageScore 3.0, so don&apos;t be surprised if your lender
              gives you a score that&apos;s different from your VantageScore. (And your VantageScore
              3.0 may differ from your score under other types of VantageScores). Just remember that
              your associated risk level is often the same even if the number is not. For some
              consumers, however, the risk assessment of VantageScore 3.0 could vary, sometimes
              substantially, from a lender’s score. If the lender&apos;s score is lower than your
              VantageScore 3.0, it is possible that this difference can lead to higher interest
              rates and sometimes credit denial.
            </Paragraph>
          </TermsContainer>
          <ModalFooter>
            <SecondaryButton onClick={onRequestClose} disabled={isAcceptingTerms}>
              Decline
            </SecondaryButton>
            <PrimaryButton onClick={acceptTermAndConditionsCallback} disabled={isAcceptingTerms}>
              Accept
            </PrimaryButton>
          </ModalFooter>
        </Wrapper>
      </Modal>
    </>
  );
};

export default Terms;
